import React, { useState, useEffect, useContext, useRef } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import ThemeContext from '../../contexts/ThemeContext';
import FunctionsContext from '../../contexts/FunctionsContext';
import Label from '../Label';
import ChipGroup from '../ChipGroup';
import UsergroupSelect from '../UsergroupSelect';
import { getUsergroups } from '../../services/usergroupService';
import { Section, ErrorText } from '../PrebookingSharedComponents';

import i18n from '../../i18n';

const SearchContainer = styled.div`
  margin-top: 8px;
`;

const CostAllocation = ({
  formData,
  updateField,
  isCostAllocationMandatory,
  error,
  clearError,
}) => {
  const { colors } = useContext(ThemeContext);
  const { sessionExpired } = useContext(FunctionsContext);
  const [userGroups, setUserGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSearchAllowed, setIsSearchAllowed] = useState(false);

  const costAllocationContainerRef = useRef(null);
  const searchContainerRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    const fetchUserGroups = async () => {
      setLoading(true);
      try {
        const response = await getUsergroups(null, true);
        if (!isMounted) return;
        const groups = Array.isArray(response) ? response : response.groups || [];
        setUserGroups(groups);
        if (!Array.isArray(response) && response.options) {
          setIsSearchAllowed(!!response.options.allowSearch);
        }
      } catch (e) {
        if (!isMounted) return;
        console.error('Failed to fetch user groups:', e);
        if (e.message === 'Session expired') {
          sessionExpired();
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchUserGroups();
    return () => { isMounted = false; };
  }, [sessionExpired, updateField]);

  // Auto-select single group when loaded
  useEffect(() => {
    if (!loading && userGroups.length === 1 && !formData.costAllocation) {
      const singleGroup = userGroups[0];
      updateField('costAllocation', singleGroup.id);
      updateField('costAllocationGroup', {
        id: singleGroup.id,
        name: singleGroup.publicName || singleGroup.name,
        invoiceRecipientRef: singleGroup.invoiceRecipientRef,
      });
    }
  }, [userGroups, loading, formData.costAllocation, updateField]);

  const searchUserGroups = async (query) => {
    try {
      const response = await getUsergroups(query);
      return Array.isArray(response) ? response : response.groups || [];
    } catch (e) {
      if (e.message === 'Session expired') { sessionExpired(); }
      return [];
    }
  };

  const costAllocationOptions = [
    ...userGroups.map(group => ({
      value: group.id,
      label: `${group.name}${group.invoiceRecipientRef ? ` (${group.invoiceRecipientRef})` : ''}`,
    })),
    ...(isSearchAllowed ? [{ value: 'other', label: i18n('other') }] : []),
  ];

  // Auto-detect edit flow: if costAllocation doesn't match any loaded group and search is allowed,
  // switch to 'other' to show search with pre-populated value
  useEffect(() => {
    if (loading || !isSearchAllowed) return;
    if (!formData.costAllocation || formData.costAllocation === 'other') return;
    const matchesLoadedGroup = userGroups.some(g => g.id === formData.costAllocation);
    if (!matchesLoadedGroup && formData.costAllocationGroup?.id) {
      updateField('costAllocation', 'other');
    }
  }, [loading, isSearchAllowed, userGroups, formData.costAllocation, formData.costAllocationGroup, updateField]);

  return (
    <Section ref={costAllocationContainerRef}>
      <Label text={i18n('cost_allocation')} required={isCostAllocationMandatory} />
      {loading ? (
        <div style={{ padding: '8px', color: colors.placeholderColor, fontSize: '14px' }}>
          {i18n('loading') || 'Loading...'}
        </div>
      ) : (
        <>
          <ChipGroup
            options={costAllocationOptions}
            selectedValues={formData.costAllocation}
            onChange={(value) => {
              updateField('costAllocation', value);

              if (value && error) {
                clearError('costAllocation');
              }

              if (value === 'other') {
                updateField('costAllocationGroup', null);
              } else {
                const matchedGroup = userGroups.find(g => g.id === value);
                if (matchedGroup) {
                  updateField('costAllocationGroup', {
                    id: matchedGroup.id,
                    name: matchedGroup.publicName || matchedGroup.name,
                    invoiceRecipientRef: matchedGroup.invoiceRecipientRef
                  });
                }
              }
            }}
          />
          {formData.costAllocation === 'other' && (
            <SearchContainer ref={searchContainerRef}>
              <UsergroupSelect
                containerRef={costAllocationContainerRef}
                scrollContainerRef={searchContainerRef}
                selected={formData.costAllocationGroup}
                onChange={(group) => {
                  updateField('costAllocationGroup', group);
                  if (error) {
                    clearError('costAllocation');
                  }
                }}
                getSelectableItems={searchUserGroups}
              />
            </SearchContainer>
          )}
        </>
      )}
      {error && <ErrorText>{error}</ErrorText>}
    </Section>
  );
};

CostAllocation.propTypes = {
  formData: PropTypes.object.isRequired,
  updateField: PropTypes.func.isRequired,
  isCostAllocationMandatory: PropTypes.bool,
  error: PropTypes.string,
  clearError: PropTypes.func.isRequired,
};

export default CostAllocation;
